import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Layers, Loader2, Check, X, MapPin, AlertTriangle, Sparkles } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * Admin → Duplicate site review.
 *
 * Surfaces every cluster where two or more campsites share a normalised
 * core name AND sit within 8 km of each other. Each cluster gets a
 * recommended canonical pick (bookable + has price + has rating wins).
 * Admin can:
 *   • One-click "Keep this, delete others" per cluster
 *   • Mark a cluster as "Not duplicates" (persists across sessions)
 *   • Run a bulk auto-merge for the high-confidence (<1 km) clusters
 */
export default function DuplicateReviewCard() {
  const [clusters, setClusters] = useState([]);
  const [totalRemovable, setTotalRemovable] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoMerging, setAutoMerging] = useState(false);
  const [resolvingKey, setResolvingKey] = useState(null);
  const [picked, setPicked] = useState({}); // {clusterKey: chosen_keep_id}
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/migrations/fuzzy-duplicates");
      setClusters(data.clusters || []);
      setTotalRemovable(data.total_possible_removals || 0);
      // default picks = recommended
      const initial = {};
      (data.clusters || []).forEach((c) => { initial[c.key] = c.recommended_keep; });
      setPicked(initial);
      setLoaded(true);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  const resolve = async (cluster, action /* 'delete' | 'ignore' */) => {
    setResolvingKey(cluster.key);
    try {
      const keep_id = picked[cluster.key] || cluster.recommended_keep;
      const delete_ids = cluster.members.map((m) => m.campsite_id).filter((id) => id !== keep_id);
      const body = action === "ignore"
        ? { keep_id, delete_ids, ignore: true }
        : { keep_id, delete_ids, ignore: false };
      const { data } = await api.post("/admin/migrations/fuzzy-duplicates/resolve", body);
      if (action === "ignore") {
        toast.success("Marked as not duplicates");
      } else {
        toast.success(`Removed ${data.deleted} duplicate(s) · ${data.total_after.toLocaleString()} unique sites remain`);
      }
      // drop the cluster locally so the admin sees progress
      setClusters((prev) => prev.filter((c) => c.key !== cluster.key));
      setTotalRemovable((n) => n - (cluster.members.length - 1));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setResolvingKey(null); }
  };

  const autoMerge = async () => {
    if (!window.confirm(
      "Auto-merge every cluster where every duplicate is within 1 km of the others?\n\n" +
      "This is safe for true duplicates (OSM centroid vs Pitchup entrance is usually <500 m). " +
      "Wider clusters stay in the review list for manual inspection."
    )) return;
    setAutoMerging(true);
    try {
      const { data } = await api.post("/admin/migrations/fuzzy-duplicates/auto-merge?max_radius_km=1.0");
      toast.success(`Auto-merged ${data.clusters_resolved} obvious cluster(s) · removed ${data.removed} dup(s) · ${data.total_after.toLocaleString()} sites remain`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAutoMerging(false); }
  };

  useEffect(() => { /* lazy-load on first expand */ }, []);

  return (
    <section
      className="border-2 hairline-strong bg-white p-6 mb-10"
      data-testid="duplicate-review-card"
    >
      <header className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Layers className="w-3 h-3" /> Catalogue hygiene
          </div>
          <h2 className="font-display text-2xl mt-1 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#ff4500]" strokeWidth={1.7} />
            Suspect duplicates
          </h2>
          <p className="text-sm text-[#555] mt-1.5 max-w-2xl leading-relaxed">
            Catches near-matches the exact dedupe misses (e.g. <em>Orcaber × 3</em>) — same normalised
            name within 8 km. Each cluster shows a <strong>★ recommended keep</strong> (the bookable
            one with pricing wins).
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button
          onClick={load}
          disabled={loading}
          className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1.5"
          data-testid="scan-dups-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
          {loaded ? "Re-scan" : "Scan catalogue"}
        </button>
        {loaded && clusters.length > 0 && (
          <button
            onClick={autoMerge}
            disabled={autoMerging}
            className="btn-secondary !px-3 !py-2 text-xs inline-flex items-center gap-1.5"
            data-testid="auto-merge-btn"
            title="Bulk-resolve every cluster where all sites are within 1 km of each other"
          >
            {autoMerging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Auto-merge obvious (&lt;1 km)
          </button>
        )}
      </div>

      {loaded && (
        <div className="border hairline bg-[#fafafa] p-3 mb-5 text-[12px] flex items-center gap-3 flex-wrap" data-testid="dup-summary">
          <span className="font-display text-lg text-[#111]">{clusters.length}</span>
          <span className="text-[#555]">suspect cluster{clusters.length === 1 ? "" : "s"}</span>
          <span className="mono text-[#888]">·</span>
          <span className="font-display text-lg text-[#111]">{totalRemovable}</span>
          <span className="text-[#555]">possible removal{totalRemovable === 1 ? "" : "s"}</span>
        </div>
      )}

      {loaded && clusters.length === 0 && !loading && (
        <div className="p-8 border hairline bg-[#f4fff0] text-center text-sm text-[#0a8500]" data-testid="no-dups">
          ✓ No suspect duplicates left to review.
        </div>
      )}

      <div className="space-y-4" data-testid="dup-cluster-list">
        {clusters.map((cluster) => (
          <DupClusterRow
            key={cluster.key}
            cluster={cluster}
            picked={picked[cluster.key]}
            onPick={(id) => setPicked((p) => ({ ...p, [cluster.key]: id }))}
            onDelete={() => resolve(cluster, "delete")}
            onIgnore={() => resolve(cluster, "ignore")}
            busy={resolvingKey === cluster.key}
          />
        ))}
      </div>
    </section>
  );
}

function DupClusterRow({ cluster, picked, onPick, onDelete, onIgnore, busy }) {
  const distColor = cluster.max_distance_km <= 1
    ? "bg-[#f4fff0] text-[#0a8500]"
    : cluster.max_distance_km <= 3
      ? "bg-[#fff8e0] text-[#a37000]"
      : "bg-[#fff4f0] text-[#b00020]";

  return (
    <div className="border hairline p-4 bg-white" data-testid={`dup-cluster-${cluster.key.replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="mono text-[10px] uppercase tracking-wider text-[#888]">key</span>
          <span className="font-display text-base break-words">{cluster.key}</span>
          <span className={`mono text-[10px] uppercase px-1.5 py-0.5 ${distColor}`}>
            ≤ {cluster.max_distance_km} km apart
          </span>
          <span className="mono text-[10px] text-[#888]">{cluster.size} sites</span>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        {cluster.members.map((m) => (
          <label
            key={m.campsite_id}
            className={`flex items-start gap-2.5 p-2.5 border hairline cursor-pointer transition-colors ${
              picked === m.campsite_id ? "bg-[#fff8f4] border-[#ff4500]" : "bg-white hover:bg-[#fafafa]"
            }`}
            data-testid={`dup-member-${m.campsite_id}`}
          >
            <input
              type="radio"
              name={`keep-${cluster.key}`}
              checked={picked === m.campsite_id}
              onChange={() => onPick(m.campsite_id)}
              className="mt-1 accent-[#ff4500] flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display text-sm break-words">{m.name}</span>
                {m.is_recommended && (
                  <span className="mono text-[9px] uppercase bg-[#ff4500] text-white px-1.5 py-0.5">★ Recommended</span>
                )}
                {m.pitchup_url_ok && (
                  <span className="mono text-[9px] uppercase bg-[#f4fff0] text-[#0a8500] px-1.5 py-0.5">✓ Bookable</span>
                )}
                {(m.price_per_night || 0) > 0 && (
                  <span className="mono text-[9px] uppercase bg-[#fff8e0] text-[#a37000] px-1.5 py-0.5">£{m.price_per_night}/nt</span>
                )}
                {(m.rating_count || 0) > 0 && (
                  <span className="mono text-[9px] text-[#888]">★ {m.rating?.toFixed?.(1) || m.rating} · {m.rating_count} review{m.rating_count === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="text-[11px] text-[#555] mt-1 flex items-center gap-1.5 flex-wrap">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="mono">{m.lat?.toFixed?.(4)}, {m.lng?.toFixed?.(4)}</span>
                {m.region && <><span>·</span><span className="break-words">{m.region}</span></>}
                <span className="mono text-[10px] text-[#999]">source={m.source}</span>
              </div>
              <a
                href={`/campsites/${m.campsite_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-[#ff4500] underline mono mt-1 inline-block"
                onClick={(e) => e.stopPropagation()}
              >
                Open campsite →
              </a>
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap pt-2 border-t hairline">
        <button
          onClick={onDelete}
          disabled={busy}
          className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1.5"
          data-testid={`dup-delete-${cluster.key.replace(/\s+/g, '-')}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Keep selected · delete {cluster.size - 1} other{cluster.size - 1 === 1 ? "" : "s"}
        </button>
        <button
          onClick={onIgnore}
          disabled={busy}
          className="btn-secondary !px-3 !py-2 text-xs inline-flex items-center gap-1.5"
          data-testid={`dup-ignore-${cluster.key.replace(/\s+/g, '-')}`}
          title="Mark as not duplicates so this cluster stops showing in future scans"
        >
          <X className="w-3.5 h-3.5" /> Not duplicates
        </button>
      </div>
    </div>
  );
}
